import { HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { AuthService } from './auth.service';
import { HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { AuthErrorCode, RefreshResponse } from './auth.models';

/** Login and refresh answer 401 on their own merits; retrying them would recurse. */
const isAuthEndpoint = (url: string): boolean => url.includes('/auth/jwt/');

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const injector = inject(Injector);
  const token = localStorage.getItem('auth_token');

  const authReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authReq).pipe(
    catchError(error => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      const authService = injector.get(AuthService);

      // Checked before the auth-endpoint guard: a superseded session is refused everywhere.
      if (error?.error?.code === AuthErrorCode.SESSION_SUPERSEDED) {
        authService.forceLogout(AuthErrorCode.SESSION_SUPERSEDED);

        return throwError(() => error);
      }

      if (isAuthEndpoint(req.url)) {
        return throwError(() => error);
      }

      return authService.refreshToken().pipe(
        switchMap((res: RefreshResponse) => {
          const cloned = req.clone({
            setHeaders: { Authorization: `Bearer ${res.access}` },
          });
          return next(cloned);
        }),
        catchError(refreshError => {
          authService.logout();
          return throwError(() => refreshError);
        })
      );
    })
  );
};
